import { useMemo, useState } from 'react';

import { useAuth } from '@/app/providers/authContext';
// Submodule paths, not barrels: `@/components/layout` and `@/features/cart` both reach back into
// this feature (the shell's routes, `CartLineRow`'s `ProductImage`), so importing either index
// here would close a cycle between them.
import { profilesForNavigationItem } from '@/components/layout/navigation';
import { useToast } from '@/components/ui';
import type { Cart, CartLineCompliance } from '@/features/cart/contracts';
import {
  useCartQuery,
  useRemoveCartItemMutation,
  useUpsertCartItemMutation,
} from '@/features/cart/queries';
import { useCurrentEstablishment } from '@/features/organization/queries';
import { useItemHistoryQuery, useOrdersQuery, useReorderMutation } from '@/features/orders/queries';
import { describeReorderReport } from '@/features/orders/reorderReport';
import { toAmount, todayInSaoPaulo } from '@/features/pricing';

import type { CatalogItem } from './contracts';
import { launchFlight } from './reorderFlight';
import type { ShoppingListBuying } from './ShoppingList';
import { historyWindowStart, usualQuantity, type UsualQuantity } from './usual';

export interface ShoppingBuying {
  /** Absent for a profile that cannot reach the cart: the list is then a price list. */
  readonly buying: ShoppingListBuying | undefined;
  readonly cart: Cart | undefined;
  /** The most recent order, for "Repetir último pedido"; undefined until known or when none. */
  readonly lastOrder: { readonly id: string; readonly code: string } | undefined;
  readonly repeatLastOrder: (origin?: Element | null) => void;
  readonly repeating: boolean;
}

/**
 * Everything the list needs to take quantities: the open cart, what this customer usually buys
 * (the last 90 days of its own order history) and the writes. Only a profile the route guard
 * admits to the cart gets any of it — for the others no cart, history or order request is made at
 * all, since each would come back 403.
 */
export function useShoppingBuying(items: readonly CatalogItem[]): ShoppingBuying {
  const toast = useToast();
  const { profile } = useAuth();
  const canBuy = profile !== null && profilesForNavigationItem('cart').includes(profile);
  const { establishmentId: current } = useCurrentEstablishment();
  const establishmentId = canBuy ? current : undefined;

  const cart = useCartQuery(establishmentId);
  const upsert = useUpsertCartItemMutation(establishmentId);
  const remove = useRemoveCartItemMutation(establishmentId);
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());

  const [historyWindow] = useState(() => {
    const today = todayInSaoPaulo();
    return { from: historyWindowStart(today), to: today };
  });
  const history = useItemHistoryQuery(historyWindow, canBuy);
  const orders = useOrdersQuery({}, establishmentId);
  const reorder = useReorderMutation(establishmentId);

  const quantities = useMemo(() => {
    const map = new Map<string, number>();
    for (const line of cart.data?.items ?? []) {
      map.set(line.sku.id, toAmount(line.quantity) ?? 0);
    }
    return map;
  }, [cart.data]);

  const compliance = useMemo(() => {
    const map = new Map<string, CartLineCompliance>();
    for (const line of cart.data?.items ?? []) {
      map.set(line.sku.id, line.compliance);
    }
    return map;
  }, [cart.data]);

  const usual = useMemo(() => {
    const map = new Map<string, UsualQuantity>();
    if (!canBuy) {
      return map;
    }
    const bySku = new Map(items.map((item) => [item.sku.id, item.sku]));
    for (const row of history.data?.rows ?? []) {
      const sku = bySku.get(row.sku_id);
      if (sku === undefined) {
        continue;
      }
      const suggestion = usualQuantity(row, sku.minimum_order_quantity, sku.order_multiple);
      if (suggestion !== null) {
        map.set(row.sku_id, suggestion);
      }
    }
    return map;
  }, [canBuy, history.data, items]);

  const settle = (skuId: string) => {
    setPending((set) => {
      const next = new Set(set);
      next.delete(skuId);
      return next;
    });
  };

  const onCommit = (skuId: string, quantity: number) => {
    if (establishmentId === undefined) {
      return;
    }
    setPending((set) => new Set(set).add(skuId));
    if (quantity > 0) {
      upsert.mutate(
        { skuId, establishmentId, quantity },
        {
          onError: () => {
            toast.error('Não foi possível atualizar a quantidade');
          },
          onSettled: () => {
            settle(skuId);
          },
        },
      );
    } else if (quantities.has(skuId)) {
      remove.mutate(
        { skuId, establishmentId },
        {
          onError: () => {
            toast.error('Não foi possível tirar o item do pedido');
          },
          onSettled: () => {
            settle(skuId);
          },
        },
      );
    } else {
      settle(skuId);
    }
  };

  const latest = orders.data?.pages[0]?.items[0];

  return {
    buying: canBuy
      ? {
          quantities,
          compliance,
          usual,
          pending,
          onCommit,
          lines: cart.data?.items,
          summary: cart.data?.summary,
          // Known only once the order history has answered: `undefined` while it loads.
          hasOrdered: orders.data === undefined ? undefined : latest !== undefined,
        }
      : undefined,
    cart: cart.data,
    lastOrder: latest === undefined ? undefined : { id: latest.id, code: latest.code },
    repeating: reorder.isPending,
    /** `origin`: the button pressed — the items fly from it into "No seu pedido" on success. */
    repeatLastOrder: (origin?: Element | null) => {
      if (latest === undefined) {
        return;
      }
      reorder.mutate(latest.id, {
        onSuccess: (result) => {
          launchFlight(origin);
          // The summary waits for the items to land, so it reads as the result, not a race.
          window.setTimeout(
            () => {
              toast.success(
                `Itens do ${latest.code} no pedido`,
                describeReorderReport(result.report),
              );
            },
            origin == null ? 0 : 650,
          );
        },
        onError: () => {
          toast.error('Não foi possível repetir o último pedido');
        },
      });
    },
  };
}
